/**
 * 位置和导航上下文管理
 *
 * 这个文件负责管理应用程序的位置和导航相关状态，包括：
 * - 当前页面路径管理
 * - 侧边栏显示状态
 * - 额外导航配置
 * - 应用程序配置信息
 */

import { createContext, ReactNode, useState } from 'react'

/**
 * 位置上下文类型定义
 */
type locationContextType = {
  /** 当前页面路径数组 */
  current: Array<string>
  /** 设置当前页面路径 */
  setCurrent: (newState: Array<string>) => void
  /** 是否打开堆叠模式 */
  isStackedOpen: boolean
  /** 设置堆叠模式状态 */
  setIsStackedOpen: (newState: boolean) => void
  /** 是否显示侧边栏 */
  showSideBar: boolean
  /** 设置侧边栏显示状态 */
  setShowSideBar: (newState: boolean) => void
  /** 额外导航配置 */
  extraNavigation: {
    title: string
    options?: Array<{
      name: string
      href: string
      icon: any
      children?: Array<any>
    }>
  }
  /** 设置额外导航配置 */
  setExtraNavigation: (newState: {
    title: string
    options?: Array<{
      name: string
      href: string
      icon: any
      children?: Array<any>
    }>
  }) => void
  /** 额外组件 */
  extraComponent: any
  /** 设置额外组件 */
  setExtraComponent: (newState: any) => void
  /** 应用程序配置 */
  appConfig: {
    mode?: number
    isDev?: boolean
    libAccepts: any
    officeUrl?: string
    dialogTips?: any
    dialogQuickSearch?: any
    websocketHost?: any
    baiyingUrl?: string // 联想百应企管后台接口地址
    baiyingApiUrl?: string // 联想百应企管后台接口地址
    baiyingFilesUrl?: string // 百应云空间接口地址
    baiyingEnterPriseConfigUrl?: string // 联想账号注销接口地址
  }
  /** 设置应用程序配置 */
  setAppConfig: (data: any) => void
}

/**
 * 位置上下文的初始值
 */
const initialValue = {
  // 从当前URL路径获取初始路径数组
  current: window.location.pathname.replace(/\/$/g, '').split('/'),
  // 根据屏幕宽度和路径判断是否打开堆叠模式
  isStackedOpen: !!(window.innerWidth > 1024 && window.location.pathname.split('/')[1]),
  setCurrent: () => {},
  setIsStackedOpen: () => {},
  // 根据路径判断是否显示侧边栏
  showSideBar: !!window.location.pathname.split('/')[1],
  setShowSideBar: () => {},
  extraNavigation: { title: '' },
  setExtraNavigation: () => {},
  extraComponent: <></>,
  setExtraComponent: () => {},
  appConfig: { libAccepts: [] },
  setAppConfig: () => {}
}

// 创建位置上下文
export const locationContext = createContext<locationContextType>(initialValue)

/**
 * 位置提供者组件
 *
 * @param children - 子组件
 * @returns 提供位置和导航相关状态和方法的上下文提供者
 */
export function LocationProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState(initialValue.current)
  const [isStackedOpen, setIsStackedOpen] = useState(initialValue.isStackedOpen)
  const [showSideBar, setShowSideBar] = useState(initialValue.showSideBar)
  const [extraNavigation, setExtraNavigation] = useState({ title: '' })
  const [extraComponent, setExtraComponent] = useState(<></>)
  const [appConfig, setAppConfig] = useState<any>({
    libAccepts: []
  })

  return (
    <locationContext.Provider
      value={{
        isStackedOpen,
        setIsStackedOpen,
        current,
        setCurrent,
        showSideBar,
        setShowSideBar,
        extraNavigation,
        setExtraNavigation,
        extraComponent,
        setExtraComponent,
        appConfig,
        setAppConfig
      }}
    >
      {children}
    </locationContext.Provider>
  )
}
